import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

export type VoidConfirmationModalProps = {
  isOpen: boolean;
  title: string;
  description: ReactNode;
  confirmText?: string;
  showReasonField?: boolean;
  reasonMaxLength?: number;
  error?: string | null;
  isSubmitting?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
};

const VoidConfirmationModal = ({
  isOpen,
  title,
  description,
  confirmText = 'Void',
  showReasonField = false,
  reasonMaxLength = 500,
  error = null,
  isSubmitting = false,
  onConfirm,
  onCancel,
}: VoidConfirmationModalProps) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 z-[70] bg-gray-600/50 overflow-y-auto h-full w-full">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md p-6 border shadow-lg rounded-md bg-white">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>

            <h3 className="text-lg font-medium text-gray-900 mt-4">{title}</h3>

            <div className="mt-2 text-sm text-gray-600">{description}</div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm text-left">
                {error}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {showReasonField && (
              <div className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, reasonMaxLength))}
                  rows={3}
                  placeholder="e.g. Duplicate entry"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={isSubmitting}
                />
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Voiding…' : confirmText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VoidConfirmationModal;
